import { z } from "zod";

import { validatePassword } from "~/utils/password";
import {
  LOCALE_OPTIONS,
  TIMEZONE_OPTIONS,
  UNIT_OPTIONS,
} from "~/utils/regional-options";

const requiredText = (label: string) =>
  z.string().trim().min(1, `${label} is required.`);
const email = z.string().trim().email("Enter a valid email address.");
const strongPassword = z.string().superRefine((value, context) => {
  for (const message of validatePassword(value).errors) {
    context.addIssue({ code: "custom", message });
  }
});

export const loginSchema = z.object({
  email,
  password: requiredText("Password"),
  captchaToken: z.string(),
});

export const totpCodeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code."),
});

export const registerSchema = z
  .object({
    firstName: requiredText("First name"),
    lastName: z.string().trim(),
    email,
    country: z.string().trim(),
    password: strongPassword,
    confirmPassword: requiredText("Password confirmation"),
    captchaToken: z.string(),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

export const emailSchema = z.object({
  email,
  captchaToken: z.string(),
});

export const passwordResetSchema = z
  .object({
    password: strongPassword,
    confirmPassword: requiredText("Password confirmation"),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

export const feedbackSchema = z.object({
  name: requiredText("Name"),
  email,
  type: z.enum(["bug", "feature", "praise"]),
  subject: z.string().trim(),
  message: requiredText("Message"),
  gdprConsent: z
    .boolean()
    .refine((value) => value, "You must agree to the Privacy Policy."),
  captchaToken: z.string(),
});

export const profileSchema = z.object({
  firstName: requiredText("First name"),
  lastName: z.string().trim(),
  units: z
    .string()
    .refine(
      (value) => UNIT_OPTIONS.some((option) => option.value === value),
      "Choose a supported unit system.",
    ),
  dateFormat: z
    .string()
    .refine(
      (value) => LOCALE_OPTIONS.some((option) => option.value === value),
      "Choose a supported locale.",
    ),
  timezone: z
    .string()
    .refine(
      (value) => TIMEZONE_OPTIONS.some((option) => option.value === value),
      "Choose a supported timezone.",
    ),
});

export const changePasswordSchema = z.object({
  currentPassword: requiredText("Current password"),
  newPassword: strongPassword,
});

export const passwordSchema = z.object({
  password: requiredText("Password"),
});

export const deleteConfirmationSchema = z.object({
  confirmation: z
    .string()
    .refine(
      (value): boolean => value === "DELETE",
      'Type "DELETE" to confirm.',
    ),
});
